import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Download, ExternalLink, Copy, Lock, Check, MessageCircle, Settings, Printer, Bookmark, LifeBuoy, RefreshCw } from 'lucide-react';
import { SEO } from '../../components/common/SEO';
import { Header } from '../../components/layout/Header';
import { API, SUPPORT_WA, PREMIUM_AMOUNT, waLink, imgSrc, downloadFile, inputCls, copyText } from '../../components/smartqr/smartqrApi';
import { ServicesPromo } from '../../components/smartqr/ServicesPromo';
import { PremiumSuccessModal } from '../../components/smartqr/PremiumSuccessModal';

const copy = async (text, msg = 'Copied!') => { (await copyText(text)) ? toast.success(msg) : toast.error('Copy failed — long-press to copy manually'); };

const PremiumCard = ({ data, onSubmitPayment, onStartPayment, onCheckStatus }) => {
  const [paymentId, setPaymentId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const p = data.payment;
  const linkStarted = p?.plink_id && ['created', 'pending'].includes(p.payment_status);

  const run = async (fn) => { setBusy(true); await fn(); setBusy(false); };
  const submit = async () => {
    if (!paymentId.trim()) return toast.error('Enter your Razorpay Payment ID');
    run(() => onSubmitPayment(paymentId.trim()));
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-orange-300 p-5" data-testid="premium-card">
      <p className="text-[11px] font-bold text-orange-600 tracking-wide">SMART QR PREMIUM</p>
      <p className="text-2xl font-extrabold text-slate-800 mb-1">₹{PREMIUM_AMOUNT} <span className="text-sm font-semibold text-slate-500">ONE TIME</span></p>
      <p className="text-sm font-semibold text-slate-700">Want to update your business anytime?</p>
      <p className="text-xs text-slate-500 mb-4">Edit details, add up to 50 products, product WhatsApp enquiries, logo & links, branded QR poster. No subscription.</p>

      {p?.payment_status === 'pending' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 text-sm" data-testid="payment-pending">
          <p className="font-semibold text-amber-800 flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Payment verification pending</p>
          <p className="text-xs text-amber-700 mt-1">{p.reason}. Premium unlocks here automatically once confirmed.</p>
        </div>
      )}
      {['failed', 'cancelled'].includes(p?.payment_status) && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-sm" data-testid="payment-failed">
          <p className="font-semibold text-red-700">Payment was not completed.</p>
          <p className="text-xs text-red-600 mt-1">{p.reason}</p>
        </div>
      )}

      <button onClick={() => run(onStartPayment)} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 disabled:opacity-60" data-testid="unlock-premium-btn">
        <Lock className="w-4 h-4" /> {busy ? 'Please wait...' : ['failed', 'cancelled'].includes(p?.payment_status) ? 'TRY AGAIN' : linkStarted ? `PAY ₹${PREMIUM_AMOUNT} ON RAZORPAY` : `UNLOCK PREMIUM — ₹${PREMIUM_AMOUNT}`}
      </button>
      <p className="text-[11px] text-slate-500 text-center mt-2">Opens your secure Razorpay payment link for this business. Use your business mobile number at checkout.</p>

      {linkStarted && (
        <button onClick={() => run(onCheckStatus)} disabled={busy} className="mt-3 w-full bg-slate-800 hover:bg-slate-900 text-white py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="check-payment-btn">
          <RefreshCw className="w-4 h-4" /> I HAVE PAID — CHECK STATUS
        </button>
      )}

      <button onClick={() => setShowForm(!showForm)} className="mt-3 w-full text-xs text-slate-500 hover:text-slate-700 underline" data-testid="payment-id-toggle">Paid but not activated? Enter your Razorpay Payment ID</button>
      {showForm && (
        <div className="mt-3 border-t border-slate-100 pt-3" data-testid="payment-id-form">
          <p className="text-xs text-slate-500 mb-2">Shown on Razorpay's success screen / SMS. Looks like <span className="font-mono">pay_XXXXXXXXXXXXXX</span>. It is verified with Razorpay before activation.</p>
          <div className="flex gap-2">
            <input value={paymentId} onChange={(e) => setPaymentId(e.target.value)} className={inputCls} placeholder="pay_XXXXXXXXXXXXXX" maxLength={18} data-testid="payment-id-input" />
            <button onClick={submit} disabled={busy} className="bg-slate-800 text-white px-4 rounded-lg font-semibold text-sm hover:bg-slate-900 disabled:opacity-60 whitespace-nowrap" data-testid="payment-id-submit-btn">{busy ? '...' : 'Verify'}</button>
          </div>
        </div>
      )}
      <p className="text-[11px] text-slate-400 mt-3">Premium is activated only after Razorpay confirms a captured ₹{PREMIUM_AMOUNT} payment for this business.</p>
    </div>
  );
};

export const MyQRPage = () => {
  const { businessId, ownerKey } = useParams();
  const [params] = useSearchParams();
  const isNew = params.get('new') === '1';
  const returnedFromRazorpay = params.get('rzp') === '1';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [celebrate, setCelebrate] = useState(false);
  const onPaid = () => { toast.success('🎉 Premium activated!'); setCelebrate(true); };

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/api/smart-qr/my/${businessId}/${ownerKey}`)).data); }
    catch (e) { setError(e.response?.status === 403 ? 'This My QR link is invalid.' : 'Business not found.'); }
  }, [businessId, ownerKey]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!returnedFromRazorpay) return;
    const qs = window.location.search.replace(/^\?/, '');
    axios.post(`${API}/api/smart-qr/my/${businessId}/${ownerKey}/payment-link/verify?${qs}`).then((r) => {
      if (r.data.status === 'paid') { toast.success('🎉 Premium activated!'); setCelebrate(true); }
      else if (r.data.status !== 'none') toast.info(r.data.message);
      load();
    }).catch(() => {});
  }, [returnedFromRazorpay, businessId, ownerKey, load]);

  const startPayment = async () => {
    try {
      const r = await axios.post(`${API}/api/smart-qr/my/${businessId}/${ownerKey}/payment-link`);
      if (r.data.status === 'paid') { toast.success('Premium is already active'); return load(); }
      window.open(r.data.short_url, '_blank', 'noopener');
      await load();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Could not start payment'); }
  };
  const checkStatus = async () => {
    try {
      const r = await axios.post(`${API}/api/smart-qr/my/${businessId}/${ownerKey}/payment-link/verify`);
      if (r.data.status === 'paid') onPaid(); else toast.info(r.data.message);
      await load();
    } catch { toast.error('Could not check payment status'); }
  };

  const submitPayment = async (pid) => {
    try {
      const res = await axios.post(`${API}/api/smart-qr/my/${businessId}/${ownerKey}/payment`, { payment_id: pid });
      if (res.data.status === 'paid') onPaid();
      else if (res.data.status === 'pending') toast.info('Payment ID received. Verification pending.');
      else toast.error(res.data.message);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not submit payment ID');
    }
  };

  const myUrl = window.location.origin + `/smart-business-qr/my/${businessId}/${ownerKey}`;
  const lostMsg = `Hi JustOffs, I have a Smart Business QR and need help recovering my My QR link.\nBusiness Name: ${data?.business?.business_name || ''}\nWhatsApp Number: `;

  if (error) return (
    <div className="min-h-screen bg-slate-50"><Header /><div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="myqr-error">
      <p className="text-lg font-bold text-slate-800 mb-2">{error}</p>
      <a href={waLink(SUPPORT_WA, lostMsg)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-green-500 text-white px-5 py-2.5 rounded-lg text-sm font-semibold"><LifeBuoy className="w-4 h-4" /> Lost your QR link? Contact JustOffs</a>
    </div></div>
  );
  if (!data) return <div className="min-h-screen bg-slate-50"><Header /><div className="p-10 text-center text-slate-500">Loading...</div></div>;

  const { business, premium_active } = data;
  const mgmtMsg = `Your JustOffs Smart Business QR management link is:\n${data.management_url}\nUse this link to update your business page and products anytime.\nYour QR code will stay the same.`;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-qr-page">
      <SEO title={`My Smart Business QR - ${business.business_name}`} description="Your JustOffs Smart Business QR" />
      <Header />
      {celebrate && premium_active && data.management_url && (
        <PremiumSuccessModal businessName={business.business_name} managementUrl={data.management_url} onClose={() => setCelebrate(false)} />
      )}
      <div className="max-w-xl mx-auto px-4 py-6 space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
          {isNew && <span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full mb-3">✓ CREATED</span>}
          <h1 className="text-xl font-extrabold text-slate-800">{isNew ? 'YOUR SMART BUSINESS QR IS READY' : 'MY SMART BUSINESS QR'}</h1>
          <p className="text-sm text-slate-500 mb-4" data-testid="myqr-business-name">{business.business_name}</p>
          <img src={imgSrc(data.qr_url)} alt="Smart Business QR" className="w-56 h-56 mx-auto rounded-xl border border-slate-100" data-testid="qr-image" />
          <button onClick={() => copy(data.public_url, 'Public page link copied')} className="mt-3 text-xs text-slate-500 font-mono break-all hover:text-orange-600 flex items-center justify-center gap-1 mx-auto" data-testid="public-url-text">
            {data.public_url.replace('https://', '')} <Copy className="w-3 h-3" />
          </button>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button onClick={() => { toast.info('Preparing your branded QR...'); downloadFile(`/api/smart-qr/qr/${business.public_slug}/card.png`, `justoffs-smart-qr-${business.public_slug}.png`); }} className="bg-slate-800 text-white py-3 rounded-xl font-semibold text-sm hover:bg-slate-900 flex items-center justify-center gap-2" data-testid="download-qr-btn"><Download className="w-4 h-4" /> DOWNLOAD QR</button>
            <Link to={`/b/${business.public_slug}`} className="bg-white border-2 border-slate-200 text-slate-700 py-3 rounded-xl font-semibold text-sm hover:border-orange-400 flex items-center justify-center gap-2" data-testid="view-page-btn"><ExternalLink className="w-4 h-4" /> VIEW MY PAGE</Link>
          </div>
          <p className="text-[11px] text-slate-400 mt-3">This QR is permanent. Update your details anytime — the QR never changes. Download gives you a branded QR card (JustOffs logo + your name) ready for counters, menus, cards and WhatsApp.</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4" data-testid="save-link-box">
          <p className="text-sm font-bold text-blue-900 flex items-center gap-2"><Bookmark className="w-4 h-4" /> Save your private MY QR link</p>
          <p className="text-xs text-blue-800 mt-1 mb-2">Bookmark this page. You need it to download your QR again and to unlock / manage Premium.</p>
          <div className="flex gap-2">
            <button onClick={() => copy(myUrl, 'My QR link copied')} className="flex-1 bg-white border border-blue-200 text-blue-800 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1" data-testid="copy-my-link-btn"><Copy className="w-3.5 h-3.5" /> Copy link</button>
            <a href={waLink('', `My JustOffs Smart Business QR link (keep private): ${myUrl}`)} target="_blank" rel="noreferrer" className="flex-1 bg-green-500 text-white py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1" data-testid="wa-my-link-btn"><MessageCircle className="w-3.5 h-3.5" /> Send to my WhatsApp</a>
          </div>
        </div>

        {premium_active ? (
          <div className="bg-white rounded-2xl border-2 border-green-300 p-5" data-testid="premium-active-card">
            <p className="text-lg font-extrabold text-slate-800 flex items-center gap-2"><Check className="w-5 h-5 text-green-500" /> 🎉 PREMIUM ACTIVATED</p>
            <p className="text-xs text-slate-500 mb-4">Your Smart Business QR Premium is now active. <span className="font-semibold text-green-600">Premium Active ✓</span></p>
            <div className="space-y-2">
              <a href={data.management_url} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2" data-testid="manage-page-btn"><Settings className="w-4 h-4" /> MANAGE MY BUSINESS PAGE</a>
              <button onClick={() => { toast.info('Generating poster...'); downloadFile(data.poster_url, `justoffs-qr-poster-${business.public_slug}.png`); }} className="w-full bg-slate-800 text-white py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2" data-testid="download-poster-btn"><Printer className="w-4 h-4" /> DOWNLOAD BRANDED QR POSTER</button>
              <a href={waLink('', mgmtMsg)} target="_blank" rel="noreferrer" className="w-full bg-green-500 text-white py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2" data-testid="wa-mgmt-link-btn"><MessageCircle className="w-4 h-4" /> SEND MANAGEMENT LINK ON WHATSAPP</a>
            </div>
            <p className="text-[11px] text-slate-400 mt-3 break-all">Management link: <span className="font-mono" data-testid="management-url-text">{data.management_url}</span></p>
            <button onClick={() => copy(data.management_url, 'Management link copied')} className="mt-2 text-xs text-slate-600 hover:text-orange-600 inline-flex items-center gap-1 font-semibold" data-testid="copy-mgmt-link-btn"><Copy className="w-3.5 h-3.5" /> Copy management link</button>
          </div>
        ) : (
          <PremiumCard data={data} onSubmitPayment={submitPayment} onStartPayment={startPayment} onCheckStatus={checkStatus} />
        )}

        <ServicesPromo compact />

        <div className="text-center pb-6">
          <a href={waLink(SUPPORT_WA, premium_active ? `Hi JustOffs, I have a Smart Business QR Premium account and need help recovering my management link.\nBusiness Name: ${business.business_name}\nWhatsApp Number: ` : lostMsg)} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-orange-600 inline-flex items-center gap-1" data-testid="lost-link-contact">
            <LifeBuoy className="w-3.5 h-3.5" /> Lost your {premium_active ? 'management' : 'QR'} link? Contact JustOffs
          </a>
        </div>
      </div>
    </div>
  );
};
